export enum Actions {
  FETCH_UNCONFIRMED_DATA_START = 'FETCH_UNCONFIRMED_DATA_START',
  FETCH_UNCONFIRMED_DATA = 'FETCH_UNCONFIRMED_DATA',
  FETCH_UNCONFIRMED_DATA_SUCCESS = 'FETCH_UNCONFIRMED_DATA_SUCCESS',
  FETCH_UNCONFIRMED_DATA_FAILURE = 'FETCH_UNCONFIRMED_DATA_FAILURE',
  FETCH_UNCONFIRMED_DATA_TIMEOUT = 'FETCH_UNCONFIRMED_DATA_TIMEOUT',
}

import type { ApolloError } from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import type { Language } from '@seek/melways-sites';

import type { Qualification as Qual } from 'src/components/Profile/Education/types';
import { isGraphQLAuthError } from 'src/modules/error-handling/error-handling';
import type { ReduxInjections, Action } from 'src/types';
import type * as Schema from 'src/types/graphql';

import { AnimationTimings } from '../Resume/EditResume/FileUpload/utils';
import { getCandidateIdFromCache } from '../data';

export type UnconfirmedDataAction =
  | (Action<Actions.FETCH_UNCONFIRMED_DATA_START> & {
      payload: { contextId: string };
    })
  | (Action<Actions.FETCH_UNCONFIRMED_DATA> & {
      payload: { contextId: string };
    })
  | (Action<Actions.FETCH_UNCONFIRMED_DATA_FAILURE> & {
      payload: Error;
    })
  | (Action<Actions.FETCH_UNCONFIRMED_DATA_SUCCESS> & {
      payload: {
        qualifications: Qual[];
        roles: Array<Omit<Schema.Role, 'function' | 'industry'>>;
      };
    })
  | Action<Actions.FETCH_UNCONFIRMED_DATA_TIMEOUT>;

const writeUnconfirmedDataToApolloCache = ({
  roles,
  qualifications,
  candidateId,
  api: {
    apolloClient: { cache },
  },
  zone,
  language,
  isUnifiedSite,
}: {
  roles: Array<Omit<Schema.Role, 'function' | 'industry'>>;
  qualifications: Schema.Qualification[];
  candidateId: string;
  api: ReduxInjections['api'];
  zone: Zone;
  language: Language;
  isUnifiedSite: boolean;
}) => {
  if (roles.length) {
    cache.modify({
      id: cache.identify({
        __typename: 'Candidate',
        _id: candidateId,
      }),
      fields: {
        'roles({"status":"unconfirmed"})': (existingRoles) => [
          ...existingRoles,
          ...roles,
        ],
      },
    });
  }

  if (qualifications.length) {
    const fieldKey = `qualifications({"isUnifiedSite":${isUnifiedSite},"languageCode":"${language}","status":"unconfirmed","zone":"${zone}"})`;
    cache.modify({
      id: cache.identify({
        __typename: 'Candidate',
        _id: candidateId,
      }),
      fields: {
        [fieldKey]: (existingQualifications) => [
          // @ts-expect-error
          ...existingQualifications,
          ...qualifications,
        ],
      },
    });
  }
};

export const fetchUnconfirmedDataSuccess =
  (payload: { data: Schema.HasResumeBeenParsedQuery }) =>
  ({
    dispatch,
    getState,
    api,
    appConfig,
  }: ReduxInjections<UnconfirmedDataAction>): void => {
    if (payload.data.viewer?.unconfirmedDataForContext?.completed === false) {
      const { pollAttempts } = getState().resume.parsing;
      if (pollAttempts <= 3) {
        setTimeout(
          () => {
            const { pendingResumeId: contextId } = getState().resume.parsing;
            dispatch({
              type: Actions.FETCH_UNCONFIRMED_DATA,
              payload: {
                contextId,
              },
            });
          },
          pollAttempts === 0
            ? AnimationTimings.UPLOADING + AnimationTimings.PARSING
            : AnimationTimings.PARSING,
        );
      } else {
        dispatch({
          type: Actions.FETCH_UNCONFIRMED_DATA_TIMEOUT,
        });
      }
    } else if (
      payload.data.viewer?.unconfirmedDataForContext &&
      'roles' in payload.data.viewer?.unconfirmedDataForContext &&
      'qualifications' in payload.data.viewer?.unconfirmedDataForContext
    ) {
      const { roles, qualifications } =
        payload.data.viewer?.unconfirmedDataForContext;
      const rolesFound = roles.length > 0;
      const qualificationsFound = qualifications.length > 0;
      const { zone, language, isUnifiedSite } = appConfig;

      if (rolesFound || qualificationsFound) {
        const candidateId = getCandidateIdFromCache(api.apolloClient.cache);
        writeUnconfirmedDataToApolloCache({
          roles: roles as Array<Omit<Schema.Role, 'function' | 'industry'>>,
          qualifications: qualifications as Schema.Qualification[],
          candidateId: candidateId || '',
          api,
          zone,
          language,
          isUnifiedSite,
        });
      }

      dispatch({
        type: Actions.FETCH_UNCONFIRMED_DATA_SUCCESS,
        payload: {
          roles,
          qualifications: qualifications as Qual[],
        },
        meta: {
          metrics: {
            rolesFound,
            qualificationsFound,
            noDataFound: !rolesFound && !qualificationsFound,
            dataFound: rolesFound || qualificationsFound,
            rolesAndQualificationsFound: rolesFound && qualificationsFound,
          },
        },
      });
    }
  };

export const fetchUnconfirmedDataFailure = (
  error: ApolloError,
): UnconfirmedDataAction => ({
  type: Actions.FETCH_UNCONFIRMED_DATA_FAILURE,
  payload: error,
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});
